<template>
  
  <v-dialog
    v-model     = "activationDialogIsOpen"
  >
    <v-card>

      <!-- Form -->
      <v-card-text class="pt-8">

        <Wizard 
          v-if      = "license.isActive"
          icon      = "mdi-key"
          color     = "success"
        >
          <div class="text-h5">
            Glim License Is Active
          </div>

          <v-divider class="my-2"></v-divider>

          <div class="text-caption">{{ activationKey }}</div>
          <div class="text-caption">{{ entitlements }}</div>
          <div class="text-caption">{{ expirationHint }}</div>
          
          <v-divider class="my-2"></v-divider>

        </Wizard>

        <!-- License Not Active -->
        <Wizard 
          v-else
          icon      = "mdi-key-outline"
          color     = "warning"
        >
          <div class="text-h5">
            Activate Glim License
          </div>

          <v-divider class="mt-2 mb-4"></v-divider>

          <v-form 
            v-model = "formIsValid"
            class   = "activation-form"
          >

            <!-- Key -->
            <v-text-field
              full-width
              color     = "accent"
              filled
              outlined
              dense
              label     = "Activation Key"
              v-model   = "activationKey"
              :rules    = "activationKeyRules"
              @change   = "onUpdateInstallationId"
              hide-details
            >
            </v-text-field>

            <!-- Activate Offline -->
            <v-checkbox
              label     = "Activate Offline"
              v-model   = "activateOffline"
              hide-details
              @change   = "onUpdateInstallationId"
            >
            </v-checkbox>

            <!-- EULA -->
            <v-checkbox
              v-model   = "eulaRead"
              :rules    = "eulaReadRules"
              hide-details
            >
              <template v-slot:label>
                <div>I accept the terms in the <br /><a @click.stop
                                                        href="/eula.html"
                                                        target="_blank"
                >License Agreement</a></div>
              </template>
            </v-checkbox>

            <template v-if="activateOffline">

              <!-- Installation Id -->
              <v-text-field
                class     = "mt-6"
                color     = "accent"
                filled
                outlined
                dense
                label     = "Offline Activation Code"
                :value    = "license.installationId"
                :disabled = "!activationKey"
                readonly
                hide-details

                append-icon   = "mdi-content-copy"
                @click:append = "onCopyInstallationId"
              >
              </v-text-field>

              <!-- Server Key -->
              <v-text-field
                class     = "mt-6"
                color     = "accent"
                filled
                outlined
                dense
                label     = "Server Key"
                :value    = "keyFilename"
                :disabled = "!license.installationId"
                :rules    = "keyFilenameRules"
                readonly
                hide-details

                append-icon   = "mdi-file-upload-outline"
                @click:append = "onSelectServerKey"
              >
              </v-text-field>

              <div class="text-caption text-right">https://cws.telestream.net/offlineactivation.aspx</div>

              <input
                ref         = "serverKeyInput"
                style       = "display: none"
                type        = "file"
                @change     = "onServerKeySelected"
              >

            </template>

          </v-form>

          <v-divider class="mt-4 mb-2"></v-divider>

        </Wizard>

      </v-card-text>

      <!-- Actions -->
      <v-card-actions>

        <v-spacer></v-spacer>

        <!-- Activate License -->
        <v-btn
          v-if      = "!license.isActive"
          color     = "accent"
          :disabled = "!userIsAdmin || !formIsValid"
          :loading  = "license.isWaitingForActivation"
          @click    = "onActivateLicense"
        > 
          Activate
        </v-btn>

        <!-- Deactivate License -->
        <v-btn
          v-if      = "license.isActive"
          color     = "error"
          :disabled = "!userIsAdmin"
          :loading  = "license.isWaitingForDeactivation"
          @click    = "onDeactivateLicense"
        >
          Deactivate
        </v-btn>

      </v-card-actions>

    </v-card>
  </v-dialog>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { AllowedViews } from '@/services/allowed-views-service';
import Wizard from '@/components/panes/components/Wizard';
import {
  ACTIVATE_LICENSE,
  ACTIVATE_LICENSE_OFFLINE,
  DEACTIVATE_LICENSE,
  GENERATE_INSTALLATION_ID
} from '@/utils/action-types';

// #endregion Imports

export default {

  // #region Properties

  components: {
    Wizard
  },
  data() {
    return {
      activationDialogIsOpen: false,
      formIsValid:            false,

      activationKey:      undefined,
      activationKeyRules: [
        k => !!k || 'A key is required.'
      ],

      activateOffline: false,

      eulaRead:      false,
      eulaReadRules: [
        r => !!r || 'Agreeing to the EULA is required.'
      ],

      keyFilename:      undefined,
      keyFilenameRules: [
        f => this.activateOffline ? !!f || 'A key file is required.' : false
      ],
      keyFileContents: undefined
    };
  },
  computed: {
    ...mapState(['license', 'uiAccess', 'user']),
    entitlements() {
      if (!this.license.isActive) {
        return;
      }

      return `Special Entitlements: ${this.license.entitlements ? this.license.entitlements.join(', ') : 'None'}`;
    },
    expirationHint() {
      if (!this.license.isActive) {
        return;
      }

      return `Expires on ${this.license.expiration.toLocaleString('en-US')}`;
    },
    userIsAdmin() {
      return this.uiAccess.has(AllowedViews.Admin) || this.user.hasAccessTo.has(AllowedViews.Admin);
    }
  },

  // #endregion Properties

  // #region Methods

  methods: {

    // #region Events

    onActivateLicense() {
      if (this.activateOffline) {
        this.$store.dispatch(
          ACTIVATE_LICENSE_OFFLINE, 
          {
            key:       this.activationKey,
            serverKey: this.keyFileContents
          }
        );
      } else {
        this.$store.dispatch(ACTIVATE_LICENSE, this.activationKey);
      }
    },

    onDeactivateLicense() {
      this.$store.dispatch(DEACTIVATE_LICENSE);
    },

    async onUpdateInstallationId() {
      if (!this.activateOffline || !this.activationKey) {
        return;
      }

      await this.$store.dispatch(GENERATE_INSTALLATION_ID, this.activationKey);
    },

    onCopyInstallationId() {
      navigator.clipboard.writeText(this.license.installationId);
    },

    onSelectServerKey() {
      this.$refs.serverKeyInput.click();
    },

    onServerKeySelected() {
      const files = this.$refs.serverKeyInput.files;
      if (files.length !== 1) {
        return;
      }

      this.keyFilename = files[0].name;
      let reader = new FileReader();
      reader.onload = () => {
        this.keyFileContents = reader.result;
      };
      reader.readAsText(files[0]);
    },

    // #endregion Events

    // #region Operations

    show() {
      this.activationKey = this.license.key;
      this.activateOffline = false;
      this.keyFilename = undefined;
      this.keyFileContents = undefined;

      this.activationDialogIsOpen = true;
    }

    // #endregion Operations

  }

  // #endregion Methods

}

</script>

<style lang="scss" scoped>

.activation-form {
  width:      320px;
}

</style>
